import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Hero from "../components/Hero";
import EditorialMarquee from "../components/EditorialMarquee";
import ProductCard from "../components/ProductCard";
import Testimonials from "../components/Testimonials";
import LeadMagnet from "../components/LeadMagnet";
import SocialFeed from "../components/SocialFeed";
import HotThisWeek from "../components/HotThisWeek";
import { api } from "../lib/api";
import { CATEGORIES } from "../lib/categories";
import { useSettings } from "../lib/settings";
import { ArrowUpRight, Instagram, Music } from "lucide-react";

const SHOPPABLE_CATEGORIES = CATEGORIES.filter((c) => c.id !== "all");

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [latest, setLatest] = useState([]);
  const { settings } = useSettings();

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 6)));
    api.get("/products").then((r) => setLatest(r.data.slice(0, 8)));
  }, []);

  return (
    <div data-testid="home-page">
      <Hero />
      <EditorialMarquee />

      {/* Hot this week — auto-driven by wishlist saves */}
      <HotThisWeek />

      {/* Lead magnet */}
      <LeadMagnet />

      {/* Categories — perfect 5x2 grid, gold cards with beige outline */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="text-center mb-14">
          <div className="uppercase text-xs tracking-[0.4em] text-[#5C5547]">Shop by category</div>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl gold-heading">Find your favorite thing.</h2>
          <div className="mt-6 mx-auto h-px w-24 gold-line" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 md:gap-5">
          {SHOPPABLE_CATEGORIES.map((c, i) => (
            <Link key={c.id} to={`/shop?category=${c.id}`}
              data-testid={`home-cat-${c.id}`}
              className="group fade-up relative aspect-square flex flex-col items-center justify-center p-4 rounded-2xl text-center transition-all overflow-hidden bg-[#F5F2EA] hover:bg-[#EAE4D3] hover:-translate-y-1 thick-gold"
              style={{ animationDelay: `${0.04 * i}s` }}>
              <div className="font-cat text-base md:text-lg lg:text-xl tracking-tight text-[#1A1815] font-bold leading-tight">
                {c.label}
              </div>
              <div className="mt-2 text-[10px] uppercase tracking-[0.3em] text-[#5C5547] inline-flex items-center gap-1 group-hover:text-[var(--accent-strong-deep)]">
                Explore <ArrowUpRight className="w-3 h-3" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured drops — thick gold framing */}
      <section className="bg-[#F5F2EA] border-y border-[#EAE4D3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="text-center mb-14">
            <div className="uppercase text-xs tracking-[0.4em] text-[#5C5547]">This week</div>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-7xl emerald-heading">Featured drops.</h2>
            <div className="mt-6 mx-auto h-px w-24 gold-line" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-14">
            {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} thick />)}
          </div>
        </div>
      </section>

      {/* Two brands */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
        <div className="text-center mb-14">
          <div className="uppercase text-xs tracking-[0.4em] text-[#5C5547]">Two brands</div>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-7xl emerald-heading">One slow, beautiful life.</h2>
          <div className="mt-6 mx-auto h-px w-24 gold-line" />
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          <div data-testid="brand-thisthat" className="group relative rounded-3xl overflow-hidden bg-[#FDFBF7] thick-gold">
            <div className="aspect-[4/3] overflow-hidden">
              <img src="https://images.unsplash.com/photo-1592252024957-b0454efe61a3?crop=entropy&cs=srgb&fm=jpg&w=900&q=85"
                alt={settings.brand_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="p-8">
              <div className="flex items-center gap-2 text-[var(--accent)]"><Instagram className="w-4 h-4" /><span className="uppercase text-xs tracking-[0.25em]">{settings.instagram_main}</span></div>
              <h3 className="mt-3 font-serif text-3xl gold-heading">{settings.brand_name}</h3>
              <p className="mt-3 text-[#5C5547] leading-relaxed">
                The signature brand. Heavyweight tees, ceramic mugs, linen throws, beauty essentials, jewelry & home goods —
                made in tiny batches.
              </p>
              <Link to="/shop?category=merch" data-testid="brand-thisthat-cta"
                className="mt-6 inline-flex items-center gap-2 text-sm text-[#2C2924] hover:text-[var(--accent)]">
                Shop the brand <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <div data-testid="brand-tinytunes" className="group relative rounded-3xl overflow-hidden bg-[#FDFBF7] thick-gold">
            <div className="aspect-[4/3] overflow-hidden bg-gradient-to-br from-[#F3E5AB] via-[#EAE4D3] to-[#D4AF37]">
              <img src="https://images.unsplash.com/photo-1518834107812-67b0b7c58434?w=900&q=85"
                alt="Tiny Tunes Dancer"
                className="w-full h-full object-cover mix-blend-multiply group-hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="p-8">
              <div className="flex items-center gap-2 text-[var(--accent)]"><Music className="w-4 h-4" /><span className="uppercase text-xs tracking-[0.25em]">{settings.instagram_secondary}</span></div>
              <h3 className="mt-3 font-serif text-3xl gold-heading">Tiny Tunes Dancer</h3>
              <p className="mt-3 text-[#5C5547] leading-relaxed">
                For the joyful at heart. Tutus, totes, dance journals & soft little merch for the babies, the kids
                and the kid-at-heart. A little corner of pure delight.
              </p>
              <Link to="/shop?category=tinytunes" data-testid="brand-tinytunes-cta"
                className="mt-6 inline-flex items-center gap-2 text-sm text-[#2C2924] hover:text-[var(--accent)]">
                Shop Tiny Tunes Dancer <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* Social feed */}
      <SocialFeed />

      {/* Just in */}
      <section className="bg-[#F5F2EA] border-y border-[#EAE4D3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="text-center mb-14">
            <div className="uppercase text-xs tracking-[0.4em] text-[#5C5547]">Just in</div>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-7xl gold-heading">Latest in the shop.</h2>
            <div className="mt-6 mx-auto h-px w-24 gold-line" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {latest.map((p, i) => <ProductCard key={p.id} product={p} index={i} thick />)}
          </div>
          <div className="text-center mt-12">
            <Link to="/shop" data-testid="latest-see-all"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#2C2924] text-[#FDFBF7] hover:bg-[#1A1815] text-sm">
              All products <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
